// Imports
import { faCircleInfo, faText } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tippy from '@tippyjs/react';
import React, { useCallback, useRef } from 'react';
import styled from 'styled-components/macro';
import Input from '../../../shareables/foundation/front-end/components/forms/fields/input';
import { useAppDispatch } from '../../../shareables/foundation/front-end/redux/hooks';
import tippyProps from '../../../shareables/foundation/front-end/utils/tippy-props';
import apiActions from '../../../shareables/integrations/api/redux/actions';
import {
	GetRecommendationParameters,
	GetRecommendationResponse,
} from '../../../shareables/types/api/main/domain/recommendation/get';
import { GetStartResponse } from '../../../shareables/types/api/main/start/get';
import cleanseDomain from '../../../shareables/utils/project-specific/formatting/cleanse-domain';


// Styled components
const Info = styled.span`
	color: hsl(0, 0%, 60%);
	font-size: 0.875rem;
	line-height: 1;
`;


// Define the accepted props
interface Props {
	startState: GetStartResponse;
	storybookCommunityName?: string;
}


// Function component
const NewCommunity: React.FC<Props> = ({ startState, storybookCommunityName }) => {
	// Use ref
	const domainGetter = useRef<() => string>();
	const domainSetter = useRef<(value: string) => void>();
	const communityNameGetter = useRef<() => string>();
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Handle community name blur
	const handleCommunityNameBlur = useCallback(() => {
		const domain = domainGetter.current?.();
		const communityName = communityNameGetter.current?.();
		
		if (!domain && communityName) {
			void dispatch(
				apiActions.call<GetRecommendationParameters, undefined, GetRecommendationResponse>({
					action: 'GET',
					uri: `/domain/recommendation`,
					parameters: {
						type: 'TEMPORARY_ADDRESS',
						communityName: communityName,
					},
					completion: (json) => {
						if (json.response.recommendation.length && !domainGetter.current?.()) {
							domainSetter.current?.(cleanseDomain(json.response.recommendation));
						}
					},
				})
			);
		}
	}, [dispatch]);
	
	
	// Return JSX
	return (
		<React.Fragment>
			<Input
				type='text'
				name='communityName'
				label='Community name'
				icon={faText}
				maxLength={75}
				placeholder='e.g., Example HOA'
				onBlur={handleCommunityNameBlur}
				initialValue={storybookCommunityName ?? startState.communityName}
				bindGetter={(getter) => (communityNameGetter.current = getter)}
				autoFocus
				required
			/>
			
			<Input
				type='text'
				name='websiteAddress'
				label='Website address'
				note={
					<Tippy
						{...tippyProps}
						content='This website address will be used during your trial. After subscribing, you can replace this with a custom domain!'
					>
						<Info aria-label='This website address will be used during your trial. After subscribing, you can replace this with a custom domain!'>
							<FontAwesomeIcon icon={faCircleInfo} />
						</Info>
					</Tippy>
				}
				suffix={
					(window as typeof window & { STORYBOOK?: true }).STORYBOOK
						? '.communitysite.com'
						: `.${process.env.REACT_APP__CUSTOMER_SITES_HOSTNAME}`
				}
				initialValue={startState.websiteAddress}
				maxLength={50}
				bindGetter={(getter) => (domainGetter.current = getter)}
				bindSetter={(setter) => (domainSetter.current = setter)}
				aria-live='polite'
				aria-atomic='true'
				required
			/>
		</React.Fragment>
	);
};

export default NewCommunity;
